import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Link,
  Stack,
  Avatar,
  Drawer,
  Tooltip,
  Typography,
  CardActionArea,
  IconButton,
  Paper
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
// hooks
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import useAuth from '../../hooks/useAuth';
// components
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { MHidden } from '../../components/@material-extend';
// configs
import sidebarConfig from './SidebarConfig';
import { appName } from 'config';
import { AuthUser } from '../../@types/authentication';
import { CurrentUserCard } from './CurrentUserCard';
// types

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex
    })
  }
}));

// ----------------------------------------------------------------------

interface IconCollapseProps {
  onToggleCollapse: VoidFunction;
  collapseClick: boolean;
}

const IconCollapse = ({ onToggleCollapse, collapseClick }: IconCollapseProps) => (
  <Tooltip title="Mini Menu">
    <CardActionArea
      onClick={onToggleCollapse}
      sx={{
        width: 18,
        height: 18,
        display: 'flex',
        cursor: 'pointer',
        borderRadius: '50%',
        alignItems: 'center',
        color: 'text.primary',
        justifyContent: 'center',
        border: 'solid 1px currentColor',
        ...(collapseClick && {
          borderWidth: 2
        })
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: 'currentColor',
          transition: (theme) => theme.transitions.create('all'),
          ...(collapseClick && {
            width: 0,
            height: 0
          })
        }}
      />
    </CardActionArea>
  </Tooltip>
);

interface DashboardSidebarProps {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
}

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }: DashboardSidebarProps) {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    // Close sidebar when route changes (only on mobile)
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Stack
        spacing={3}
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center'
          })
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box
            component={RouterLink}
            to="/dashboard/orders"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none'
            }}
          >
            <Logo />
            {!isCollapse && (
              <Typography
                variant="h6"
                sx={{
                  color: 'text.primary',
                  ml: 2,
                  fontWeight: 600
                }}
              >
                {appName}
              </Typography>
            )}
          </Box>

          <MHidden width="lgDown">
            <IconCollapse onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
          </MHidden>
        </Stack>

        {isCollapse ? (
          <Avatar
            alt={user?.email || 'User Avatar'}
            src="/static/mock-images/avatars/avatar_default.jpg"
            sx={{
              width: 40,
              height: 40,
              mx: 'auto',
              mb: 2
            }}
          />
        ) : (
          <CurrentUserCard user={user} onLogout={logout} />
        )}
      </Stack>

      <NavSection navConfig={sidebarConfig} isShow={!isCollapse} />
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH
        },
        ...(collapseClick && {
          position: 'absolute'
        })
      }}
    >
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
      {/*<Drawer*/}
      {/*  open={isOpenSidebar}*/}
      {/*  onClose={onCloseSidebar}*/}
      {/*  PaperProps={{*/}
      {/*    sx: {*/}
      {/*      width: DRAWER_WIDTH,*/}
      {/*      display: { xs: 'block', lg: 'none' } // Use MUI's built-in breakpoints*/}
      {/*    }*/}
      {/*  }}*/}
      {/*>*/}
      {/*  {renderContent}*/}
      {/*</Drawer>*/}

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
              ...(isCollapse && {
                width: COLLAPSE_WIDTH
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
              })
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
